import React, { PropsWithChildren, useContext, useEffect, useState } from 'react';
import { auth } from '../../lib/supabase';
// import i18n from '../../lib/i18n';
// import { TFuncKey } from 'react-i18next';
import { AuthError, Session, User } from '@supabase/supabase-js';

export function useAuth() {
    return useContext(AuthContext);
}

const authenticationFunctions = {
    signup: function (email: string, password: string, acceptEmailUpdates: boolean = false) {
        return auth.signUp({ email, password, options: { data: { initial_email_consent: acceptEmailUpdates } }});
    },

    login: function (email: string, password: string) {
        return auth.signInWithPassword({ email, password });
    },

    signInWithGoogle: function () {
        return auth.signInWithOAuth({ provider: 'google', options: { redirectTo: 'http://findmeinzuhause.de/app/overview' } });
    },

    logout: function () {
        return auth.signOut();
    },

    sendPasswordResetMail: function (email: string) {
        return auth.resetPasswordForEmail(email, { redirectTo: 'http://findmeinzuhause.de/app/#reset' });
    },

    verifyPasswordResetCode: function () {
        return auth.updateUser({})
    },

    resetPassword: function (newPassword: string) {
        return auth.updateUser({ password: newPassword });
    },
}

const defaultProps = {
    currentUser: null as User | null,
    currentSession: null as Session | null,
    authenticationNotification: null as string | null,
    setAuthenticationNotification: (_: string | null) => { },
}

type AuthContextProps = typeof authenticationFunctions & typeof defaultProps;

const AuthContext = React.createContext<AuthContextProps>({ ...authenticationFunctions, ...defaultProps });

const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [currentSession, setCurrentSession] = useState<Session | null>(null);
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [authenticationNotification, setAuthenticationNotification] = useState<string | null>(null);

    useEffect(() => {
        const subscription = auth.onAuthStateChange((event, session) => {
            if (session?.access_token !== accessToken) {
                console.log('onAuthStateChange', { event, session, user: session?.user });

                setCurrentUser(session?.user ? session.user : null);
                setCurrentSession(session);
                setAccessToken(session?.access_token || null);
            } else {
                console.log('onAuthStateChange - session is the same');
            }
        });

        auth.getSession().then(({ data, error }) => {
            if (error)
                console.error(error);
            
            const session = data.session;

            if (session?.access_token !== accessToken) {
                console.log('getSession', { session, user: session?.user });
                setCurrentSession(session);
                setCurrentUser(session?.user ? session.user : null);
                setAccessToken(session?.access_token || null);
            } else {
                console.log('getSession - session is the same');
            }
    
            setLoading(false);
        });

        return subscription.data.subscription.unsubscribe;
    }, [accessToken]);

    const _setAuthenticationNotification = function (state: string | null) {
        setAuthenticationNotification(state);
    }

    // Hide the loading spinner after finishing loading.
    useEffect(() => {
        if (loading) return;

        document.getElementById('page-loading-spinner')?.remove();
    }, [loading]);

    return (
        <AuthContext.Provider value={{
            ...authenticationFunctions,

            currentSession,
            currentUser,

            authenticationNotification,
            setAuthenticationNotification: _setAuthenticationNotification,
        }}>
            {/* Only render children if loading is false */}
            { !loading && children}
        </AuthContext.Provider>
    )
}

export default AuthProvider;

export function handleError(error: AuthError): HandledAuthError | null {
    // function t(s: TFuncKey<"translation">, f?: any) {
    //     return i18n.t(s, f);
    // }

    // Error codes for each function can be found here:
    // https://supabase.com/docs/reference/javascript/auth-error-codes
    // TODO: email_not_confirmed, over_request_rate_limit, user_banned, "Email rate limit exceeded"
    // switch (error.code) {
    //     // General
    //     case 'validation_failed':
    //         return {
    //             message: t('authentication.erros.invalidEmailAddress'),
    //             field: 'email',
    //         }
    //     case 'user_not_found':
    //         return {
    //             message: t('authentication.erros.userNotFound'),
    //             field: 'email',
    //         }
    //     case 'user_banned':
    //         return {
    //             message: t('authentication.erros.userDisabled'),
    //             field: 'email',
    //         }
    //     case 'weak_password':
    //         return {
    //             message: t('authentication.erros.weakPassword'),
    //             field: 'password',
    //         }

    //     // Login
    //     // case 'auth/wrong-password':
    //     //     return {
    //     //         message: t('authentication.erros.wrongPassword'),
    //     //         field: 'password',
    //     //     }

    //     // Registration
    //     case 'email_exists':
    //     case 'user_already_exists':
    //         return {
    //             message: t('authentication.erros.emailAlreadyInUse'),
    //             field: 'email',
    //         }

    //     // Verify reset code & Password reset & Email verification
    //     // case 'auth/expired-action-code':
    //     //     return {
    //     //         message: t('authentication.erros.expiredActionCode'),
    //     //     }
    //     // case 'auth/invalid-action-code':
    //     //     return {
    //     //         message: t('authentication.erros.invalidActionCode'),
    //     //     }

    //     default:
    //         // The following errors do not have a code:
    //         if (error.message === "Invalid login credentials")
    //             return {
    //                 message: t('authentication.erros.wrongPassword'),
    //                 field: 'password',
    //             }
                
    //         return {
    //             message: t('authentication.erros.otherError'),
    //         }
    // }
    switch (error.code) {
        // General
        case 'validation_failed':
            return {
                message: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
                field: 'email',
            }
        case 'user_not_found':
            return {
                message: 'Die E-Mail Adresse existiert nicht. Hast du bereits ein Konto erstellt?',
                field: 'email',
            }
        case 'user_banned':
            return {
                message: 'Der Benutzer ist deaktiviert. Wenden Sie sich an das FindMeinZuhause.de-Team für weitere Informationen.',
                field: 'email',
            }
        case 'weak_password':
            return {
                message: 'Das Passwort muss mindestens 6 Zeichen lang sein.',
                field: 'password',
            }

        // Registration
        case 'email_exists':
        case 'user_already_exists':
            return {
                message: 'Die E-Mail Adresse ist bereits registriert. Hast du dein Passwort vergessen?',
                field: 'email',
            }
        default:
            // The following errors do not have a code:
            if (error.message === "Invalid login credentials")
                return {
                    message: 'Die E-Mail Adresse oder das Passwort sind falsch.',
                    field: 'password',
                }

            if (error.message === "Email not confirmed")
                return {
                    message: 'Bitte bestätigen Sie Ihre E-Mail-Adresse. Überprüfen Sie Ihren Posteingang und klicken Sie auf den Bestätigungslink.',
                    field: 'email',
                }
            
            return {
                message: 'Ups... Etwas ist falsch gelaufen. Bitte versuche es später erneut oder kontaktiere das FindMeinZuhause.de Team.',
            }
    }
}

export interface HandledAuthError {
    message: string;
    field?: 'email' | 'password';
}